import { forwardRef, useCallback, useRef, useState } from 'react';

import { SearchIcon, X } from '../assets';
import { COLORS, cn } from '../lib';

interface SearchInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  onSearch?: (value: string) => void;
  onClear?: () => void;
  containerClassName?: string;
}

export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(
  ({ onSearch, onClear, containerClassName, ...props }, ref) => {
    const [isFocused, setIsFocused] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    const handleFocus = useCallback(() => {
      setIsFocused(true);
    }, []);
    const handleBlur = useCallback(() => {
      setIsFocused(false);
    }, []);

    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        onSearch?.(e.target.value);
      },
      [onSearch],
    );

    const handleClear = useCallback(() => {
      if (inputRef.current) {
        inputRef.current.value = '';
        inputRef.current.blur();
      }
      onClear?.();
    }, [onClear]);

    return (
      <div
        className={cn(
          'flex min-h-10 w-full items-center gap-2 rounded-xl bg-base-300 p-2',
          containerClassName,
        )}
      >
        <SearchIcon
          color={isFocused ? COLORS['icon-base-500'] : undefined}
          aria-hidden="true"
        />
        <input
          ref={ref ?? inputRef}
          type="text"
          {...props}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          className="w-full bg-surface-00-transparent text-base font-normal leading-5 placeholder:text-base-100 focus:outline-none"
        />
        {(isFocused || props.value) && onClear ? (
          <button onClick={handleClear} aria-label="Clear search">
            <X className="h-6 w-6" aria-hidden="true" />
          </button>
        ) : (
          <div className="h-6 w-6" />
        )}
      </div>
    );
  },
);

SearchInput.displayName = 'SearchInput';
